import type {
  ArchiefRij, Bijlagen, DraaiCel, IkAntwoord, Ik, Kubus, Lijst, LogRegel, OperationeelRij, RapportBron, RelatieBegin,
  RelatieControle, RelatieInvoer, Opzoeken, RelatieKaart, RelatieLiveAntwoord, RelatieUitkomst, StandRij, Vraag,
  WijzigUitkomst, WijzigVraag,
} from '../../gedeeld/types'
/* De indeling staat in lib/groepen.ts en niet in gedeeld/types.ts: dat bestand
   is een letterlijke kopie van de dashboard-repo, en de vorm hoort bij de
   functie die hem gebruikt. Zie de kop van groepen.ts. */
import type { Indeling } from './groepen'

/* ===========================================================================
 *  Praten met de eigen server
 *
 *  Alles loopt via de Worker op hetzelfde adres; de browser heeft nergens
 *  anders een sleutel voor. Wat hij heeft is een koekje, en dat gaat vanzelf
 *  mee.
 *
 *  Elke aanroep gooit bij een mislukking, met de tekst van de server erbij,
 *  en het scherm laat die zien. Een lege tabel tonen terwijl de vraag
 *  mislukte, is hier de duurste fout: dan lijkt het of een boeking niet
 *  bestaat.
 * =========================================================================== */

export class Fout extends Error {
  /** Per veld wat er niet klopt, als de server dat meestuurde. */
  constructor(public status: number, bericht: string, public fouten: Record<string, string> = {}) { super(bericht) }
}

let bijUitgelogd: (() => void) | null = null
export function opUitgelogd(f: () => void) { bijUitgelogd = f }

async function lees<T>(a: Response): Promise<T> {
  let lijf: { ok?: boolean; fout?: string; fouten?: Record<string, string> } & Record<string, unknown> = {}
  try { lijf = await a.json() } catch { /* geen JSON: dan volgt hieronder de status */ }
  if (a.status === 401) {
    bijUitgelogd?.()
    throw new Fout(401, 'Je bent uitgelogd.')
  }
  if (!a.ok || lijf.ok === false) throw new Fout(a.status, lijf.fout ?? `De server gaf ${a.status}.`, lijf.fouten ?? {})
  return lijf as T
}

/** Een actie van de functie exact-inzicht (zie worker/gegevens.ts voor de lijst). */
export async function x<T = Record<string, unknown>>(actie: string, lijf: object = {}, signaal?: AbortSignal): Promise<T> {
  let a: Response
  try {
    a = await fetch('/api/x', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ ...lijf, actie }),
      signal: signaal,
    })
  } catch (e) {
    if (e instanceof DOMException && e.name === 'AbortError') throw e
    throw new Fout(0, 'Geen verbinding. Controleer je internet en probeer het opnieuw.')
  }
  return lees<T>(a)
}

export interface Sessie { ik: Ik; verlooptOp: number }

/** Null als er niemand is ingelogd. Gooit niet: dit is de eerste vraag. */
export async function haalSessie(): Promise<Sessie | null> {
  try {
    const a = await fetch('/api/sessie', { headers: { Accept: 'application/json' } })
    if (!a.ok) return null
    const lijf = await a.json() as { ok?: boolean } & Sessie
    return lijf.ok ? lijf : null
  } catch {
    return null
  }
}

export async function uitloggen(): Promise<void> {
  try { await fetch('/api/uitloggen', { method: 'POST' }) } catch { /* vervalt vanzelf */ }
}

/** Een adres bij postcode + huisnummer, of vrij zoeken (worker/adres.ts, PDOK). */
export interface Adres { straat: string; nummer: string; postcode: string; plaats: string }
export async function adresZoeken(v: { postcode?: string; huisnummer?: string; q?: string }, signaal?: AbortSignal) {
  const u = new URLSearchParams()
  for (const [k, w] of Object.entries(v)) if (w) u.set(k, w)
  let a: Response
  try {
    a = await fetch('/api/adres?' + u.toString(), { headers: { Accept: 'application/json' }, signal: signaal })
  } catch (e) {
    if (e instanceof DOMException && e.name === 'AbortError') throw e
    throw new Fout(0, 'Geen verbinding.')
  }
  return lees<{ adressen: Adres[]; straat: Omit<Adres, 'nummer'> | null }>(a)
}

export const bijlageAdres = (bv: string, id: string, inline = false) => `/api/bijlage/${bv}/${id}${inline ? '?inline=1' : ''}`

export interface LijstOpties {
  sorteer?: string
  richting?: 'op' | 'af'
  pagina?: number
  perPagina?: number
  velden?: string[] | null
  export?: string
  /** false: geen totaal meesturen (het scherm vraagt het apart). */
  metTotaal?: boolean
  /** Alleen het totaal, geen regels. */
  alleenTotaal?: boolean
}

export const api = {
  ik: () => x<IkAntwoord>('ik'),
  stand: (divisies?: string[]) => x<{ stand: StandRij[] }>('stand', { divisies }).then((r) => r.stand),

  lijst: (v: Vraag, o: LijstOpties = {}, signaal?: AbortSignal) => x<Lijst>('lijst', { ...v, ...o }, signaal),
  waarden: (v: Vraag, veld: string) => x<{ waarden: { waarde: unknown; aantal: number }[] }>('waarden', { ...v, veld }).then((r) => r.waarden),
  draai: (v: Vraag, o: { rijen: string; kolommen: string | null; waarde: string }, signaal?: AbortSignal) =>
    x<{ cellen: DraaiCel[]; afgekapt: boolean }>('draai', { ...v, ...o }, signaal),
  rij: (id: string) => x<{ rij: ArchiefRij }>('rij', { id }).then((r) => r.rij),

  boeking: (division: string, entryId: string) => x<{ regels: ArchiefRij[] }>('boeking', { division, entryId }).then((r) => r.regels),
  boekingLive: (division: string, entryId: string) =>
    x<{ rijen: Record<string, unknown>[]; afgekapt: boolean }>('boeking-live', { division, entryId }),

  relaties: (divisies: string[], tekst: string) => x<{ relaties: ArchiefRij[] }>('relaties', { divisies, tekst }).then((r) => r.relaties),
  relatie: (division: string, code: string) => x<RelatieKaart>('relatie', { division, code }),

  relatieBegin: () => x<RelatieBegin>('relatie-begin'),
  relatieOpzoeken: (v: { kvk?: string; btw?: string; naam?: string; postcode?: string; huisnummer?: string }, signaal?: AbortSignal) =>
    x<Opzoeken>('relatie-opzoeken', v, signaal),
  relatieLive: (division: string, code: string) => x<RelatieLiveAntwoord>('relatie-live', { division, code }),
  relatieWijzigenNakijken: (v: WijzigVraag, signaal?: AbortSignal) =>
    x<{ wat: string[]; waarschuwingen: string[]; nieuweIban: boolean; verouderd: boolean }>('relatie-wijzigen-nakijken', v, signaal),
  relatieWijzigen: (v: WijzigVraag) => x<{ uitkomsten: WijzigUitkomst[]; wat: string[] }>('relatie-wijzigen', v),
  relatieControleren: (relatie: RelatieInvoer, divisies: string[]) => x<RelatieControle>('relatie-controleren', { relatie, divisies }),
  relatieAanmaken: (relatie: RelatieInvoer, divisies: string[], toch: string[]) =>
    x<{ relatie: RelatieInvoer; uitkomsten: RelatieUitkomst[] }>('relatie-aanmaken', { relatie, divisies, toch }),

  jaren: (divisies: string[]) => x<{ saldi: number[]; boekingen: number[] }>('jaren', { divisies }),
  kubus: (divisies: string[], jaar: number, bron: 'saldi' | 'boekingen', kostenplaats = false, signaal?: AbortSignal) =>
    x<Kubus>('kubus', { divisies, jaar, bron, kostenplaats }, signaal),

  /* ---- Inzicht: de rapportage, de groep en het overzicht ----
   *
   *  De standen per rekening, per kostenplaats en per periode, plus hetzelfde
   *  van vorig boekjaar. Dat is de kleinste vorm waaruit de hele rapportage
   *  nog te maken is; alles daarna -- een bv uitvinken, een ander
   *  periodebereik, de balans in plaats van de winst-en-verliesrekening --
   *  gebeurt in de browser. Zie lib/rapportage.ts.
   */
  rapportage: (divisies: string[], jaar: number, signaal?: AbortSignal) =>
    x<RapportBron>('rapportage', { divisies, jaar }, signaal),
  /** De groepsindeling waarop de bv's worden opgeteld (0127, 0128, 0131). */
  indeling: () => x<{ indeling: Indeling }>('indeling').then((r) => r.indeling),
  /** Wassingen, omzet en gewerkte uren per vestiging per maand (0128). */
  operationeel: (jaar: number) =>
    x<{ rijen: OperationeelRij[] }>('operationeel', { jaar }).then((r) => r.rijen),
  /** Eén rekening van één bv op een groepsregel zetten -- of de uitzondering weg. */
  indelen: (division: string, rekening: string, groepId: string | null, waarom = '') =>
    x('indelen', { division, rekening, groepId, waarom }),
  /** De vlaggen van één rubriek verzetten (0171). */
  vlaggen: (groepId: string, wat: {
    rubriek?: string | null
    rol?: string | null
    kasstroomId?: string | null
    personeel?: boolean
    direct?: boolean
  }) => x('vlaggen', { groepId, ...wat }),

  live: (v: { pad: string; division: string; filter?: string; velden?: string; sorteer?: string; aantal?: number }) =>
    x<{ rijen: Record<string, unknown>[]; afgekapt: boolean; pad: string }>('live', v),
  bijlagen: (division: string, document: string) => x<Bijlagen>('bijlagen', { division, document }),

  exportGemaakt: (v: { scherm: string; formaat: string; wat?: object; divisies: string[]; rijen: number }) =>
    x('export-gemaakt', v).catch(() => { /* een gemiste logregel houdt de export niet tegen */ }),

  logboek: () => x<{ regels: LogRegel[] }>('logboek').then((r) => r.regels),
  indexen: (maken = false) => x<{ indexen: { naam: string; sleutel: unknown }[] }>('indexen', { maken }).then((r) => r.indexen),
}
